import React, { useContext, useState, useEffect, useRef } from "react";
import { HotelIcon, TrendingDown, TrendingUp, Shield, BarChart3 } from "lucide-react";
import { ThemeContext } from "../../constant/theme";
import { LanguageContext } from "../../constant/LanguageContext";
import { useAuth } from "../../context/AuthContext";

import WelcomeCard from "../../components/WelcomeCard";
import StatsGrid from "../../components/StatsGrid";
import ActivityFeed from "./components/ActivityFeed";
import HiringTrendChart from "./components/HiringTrendChart";
import CompaniesSplitBar from "./components/CompaniesSplitBar";
import EmployeeDistributionChart from "./components/EmployeeDistributionChart";
import AttendanceDistributionChart from "./components/AttendanceDistributionChart";

import useDashboardData from "./hooks/useDashboardData";
import useDashboardStats from "./hooks/useDashboardStats";
import useAttendanceData from "./hooks/useAttendanceData";
import useScreenSize from "./hooks/useScreenSize";

import {
  getResponsiveStyles,
  getHoverStyles,
  getDefaultStyles,
} from "../../utils/styleUtils";
import { text } from "./constants/text";
import * as icons from "../../constant/icons";
import { SkeletonStyles, SkeletonBox, LoadingPill, SkeletonStatCard } from "../../components/SkeletonLoader";
import LoadErrorState from "../../components/LoadErrorState";

// Scrollbars stay functional but invisible everywhere on this page — including
// the "Other" detail modals — without hiding native scroll behavior
// (wheel/touch/keyboard still work). Shared by the skeleton and the loaded view:
// .dashboard-page has overflowX hidden, which makes it a vertical scroll
// container too, so the bar showed during loading when only the loaded view
// carried these rules.
const HIDE_SCROLLBARS_CSS = `
  .dashboard-page,
  .dashboard-page * {
    scrollbar-width: none !important;
    -ms-overflow-style: none !important;
  }
  .dashboard-page::-webkit-scrollbar,
  .dashboard-page *::-webkit-scrollbar {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
  }
`;

const DashboardContent = ({
  onRetry,
  employeeCount: propEmployeeCount,
  counts: propCounts,
  departmentStats: propDepartmentStats,
  // welcomeCard carries a -50px top tuck sized for the ADMIN shell's topbar.
  // Shells without that gap (promoted user's admin view) pass flushTop so the
  // card doesn't climb over their header.
  flushTop = false,
  // Current admin/manager view selected in the parent shell's ViewTypeSwitcher.
  // Threaded into every data hook below (and into their effect dependency
  // arrays) so toggling the switcher re-fetches scoped numbers immediately —
  // previously the hooks only read view type from localStorage once on mount,
  // so the dashboard kept showing stale numbers until a full page reload.
  viewType,
} = {}) => {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const isRTL = language === "ar";
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  const screenSize = useScreenSize();

  // ── Visibility-scoped stats (one call) + per-card admin-level gating ──
  // scopedStats is now the PRIMARY data source: one server-aggregated call
  // covers totals, departments, companies, hiring trend and activities. The
  // legacy multi-endpoint hook below only fetches as a fallback when this
  // endpoint fails. Each card beyond the KPI tiles is a dashboard feature an
  // admin level can grant — isFeatureVisible bypasses for admin/superadmin.
  const { stats: scopedStats, loading: statsLoading } = useDashboardStats(BASE_URL, viewType);
  const statsFailed = !statsLoading && !scopedStats;

  // Attendance distribution chart data (today + prev-month sample for trend).
  // Read-only no-payroll-sync endpoint; drives the AttendanceDistributionChart.
  const { attendanceData, previousMonthData } = useAttendanceData({ BASE_URL, language, viewType });

  const {
    employeeCount,
    counts,
    departmentStats,
    userName,
    userArabicName,
    loading,
    error,
    recentActivities,
  } = useDashboardData(
    propEmployeeCount,
    propCounts,
    propDepartmentStats,
    BASE_URL,
    statsFailed, // legacy fetch only runs if /dashboard-stats failed
    viewType,
  );
  const { isFeatureVisible, permissions, user: authUser } = useAuth();
  // Card gating applies ONLY to promoted users (an actual admin level). Plain
  // admins/managers keep the full dashboard exactly as before; a promoted
  // user with no dashboard features gets the default (KPI tiles only).
  const isBypassedRole = authUser?.role === "admin" || authUser?.role === "superadmin";
  const gatingActive = !isBypassedRole && !!permissions?.adminLevelId;
  const showDepartments = !gatingActive || isFeatureVisible("dashboard", "departments");
  const showAttendance = !gatingActive || isFeatureVisible("dashboard", "attendance_charts");
  const showHiring = !gatingActive || isFeatureVisible("dashboard", "hiring_trend");
  const showCompanies = !gatingActive || isFeatureVisible("dashboard", "companies_split");
  const showActivities = !gatingActive || isFeatureVisible("dashboard", "recent_activities");

  const effEmployeeCount = scopedStats ? scopedStats.total : employeeCount;
  const effCounts = scopedStats
    ? {
        ...counts,
        Active: scopedStats.active,
        OnLeave: scopedStats.onLeave,
        TotalCompanies: scopedStats.companies?.length ?? counts.TotalCompanies,
      }
    : counts;
  const effDepartmentStats = scopedStats?.departments?.length
    ? scopedStats.departments
    : departmentStats;
  const effRecentActivities = scopedStats?.recentActivities?.length
    ? scopedStats.recentActivities
    : recentActivities;

  const PALETTE = [
    "#3266ad", "#1d9e75", "#ba7517", "#993556", "#534ab7",
    "#3b6d11", "#3b8bd4", "#d85a30", "#639922", "#7f77dd",
    "#73726c", "#0f6e56", "#a32d2d", "#854f0b", "#185fa5",
  ];
  // Calculate total across departments early (needed before enriched variable)
  const departmentTotal = effDepartmentStats.reduce(
    (sum, dept) => sum + Number(dept.count || 0),
    0,
  );
  const totalAcrossDepartments =
    Number(effEmployeeCount || 0) > 0
      ? Number(effEmployeeCount || 0)
      : departmentTotal;
  // ─── Skeleton فقط في أول تحميل — مش هيظهر تاني مع كل ريفريش ─────────────────
  // Gate on the single stats call; the legacy hook's loading only matters
  // when it's actually running (fallback mode).
  const isInitialLoading = statsLoading || (statsFailed && loading);
  const [pageReady, setPageReady] = useState(false);
  useEffect(() => {
    if (!isInitialLoading) setPageReady(true);
  }, [isInitialLoading]);

  // Reset scroll on the shared layout's content pane once the skeleton swaps
  // to real content — WelcomeCard's -50px marginTop otherwise renders clipped
  // if that pane kept a leftover scroll offset from the (differently sized) skeleton.
  const containerRef = useRef(null);
  useEffect(() => {
    if (!pageReady) return;
    let node = containerRef.current?.parentElement;
    while (node && node !== document.body) {
      const overflowY = window.getComputedStyle(node).overflowY;
      if (overflowY === "auto" || overflowY === "scroll") {
        node.scrollTop = 0;
        break;
      }
      node = node.parentElement;
    }
  }, [pageReady]);

  // Define theme and styling variables
  const dark = theme === "dark";
  const chartCard = {
    flex: 1,
    height: "100%",
    boxSizing: "border-box",
    padding: "16px",
    borderRadius: "16px",
    background: dark ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,1)",
    border: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"}`,
    boxShadow: dark
      ? "0 8px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)"
      : "0 8px 32px rgba(0,0,0,0.1)",
  };
  const sectionTitle = {
    fontSize: "15 px",
    fontWeight: 700,
    color: dark ? "#f1f5f9" : "#1e293b",
    marginBottom: "12px",
    fontFamily: "'Cairo', sans-serif",
  };
  const enriched = effDepartmentStats.map((d, i) => ({
    ...d,
    color: d.color || PALETTE[i % PALETTE.length],
    pct: totalAcrossDepartments > 0 ? (d.count / totalAcrossDepartments) * 100 : 0,
  }));
  const sorted = [...enriched].sort((a, b) => b.count - a.count);
  const donutData1 = sorted.map(d => ({
    label: d.name,
    value: d.count,
    color: d.color
  }));

  const stats = [
    {
      title: text[language].totalEmployees,
      value: effEmployeeCount,
      icon: icons.Users,
      iconBackground: "rgb(0, 180, 72)",
      showSymbol: true,
      valueAlign: 50,
    },
    {
      title: text[language].activeEmployees,
      value: effCounts.Active,
      description: text[language].thisWeek,
      icon: icons.UserCheck,
      valueAlign: 50,
      iconBackground: "rgb(6, 0, 180)",
    },
    {
      title: text[language].onLeave,
      value: effCounts.OnLeave,
      description: text[language].today,
      icon: icons.Calendar,
      valueAlign: 50,
      iconBackground: "rgb(180, 0, 72)",
    },
    {
      title: text[language].totalCompanies,
      value: effCounts.TotalCompanies,
      description: text[language].ovearall,
      icon: icons.Building,
      valueAlign: 50,
      iconBackground: "rgb(180, 57, 0)",
    },
  ];

  const styles = getResponsiveStyles(screenSize, theme, language);
  if (flushTop) {
    styles.welcomeCard = { ...styles.welcomeCard, marginTop: 0 };
  }

  // Use Arabic name for Arabic language, English name for other languages
  // Names come from the server-confirmed profile (useDashboardData), never
  // from browser storage.
  const getDisplayName = () =>
    language === "ar" ? userArabicName || userName || "" : userName || userArabicName || "";

  const displayName = getDisplayName();
  const formatTwoNames = (name = "") => {
    if (!name || typeof name !== "string") return "";
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "";
    if (parts.length === 1) return parts[0];
    return `${parts[0]} ${parts[1]}`;
  };
  const displayNameShort = formatTwoNames(displayName);
  const displayTitle = displayNameShort
    ? `${text[language].welcome}, ${displayNameShort}`
    : text[language].welcome;

  // ── Role badge (end of the welcome card) ──
  // Names the capacity this dashboard is being viewed in, so it always agrees
  // with the scoped numbers below: the admin shell, a manager switched to the
  // admin view, and a promoted user's admin view (flushTop) all read as Admin;
  // a manager in their own view reads as Manager. Labels and icons match
  // ViewTypeSwitcher. The admin LEVEL is deliberately not shown here: a
  // promoted user's level is an internal personal row (__user__<id>).
  const accountRole = String(authUser?.role || localStorage.getItem("role") || "").toLowerCase();
  const inAdminCapacity =
    accountRole === "admin" || accountRole === "superadmin" || viewType === "admin" || flushTop;
  const roleBadge = inAdminCapacity
    ? {
        Icon: Shield,
        label: accountRole === "superadmin"
          ? (isRTL ? "مسؤول عام" : "Super Admin")
          : (isRTL ? "مسؤول" : "Admin"),
        color: dark ? "#7cb8ec" : "#1e5a96",
        tint: dark ? "rgba(58,143,208,0.14)" : "rgba(30,90,150,0.08)",
        ring: dark ? "rgba(58,143,208,0.30)" : "rgba(30,90,150,0.18)",
      }
    : accountRole === "manager"
      ? {
          Icon: BarChart3,
          label: isRTL ? "مدير" : "Manager",
          color: dark ? "#5fd0a4" : "#0f6e56",
          tint: dark ? "rgba(29,158,117,0.14)" : "rgba(15,110,86,0.08)",
          ring: dark ? "rgba(29,158,117,0.30)" : "rgba(15,110,86,0.18)",
        }
      : null;

  // ═══════════════════════════════════════════════════════════
  // INITIAL SKELETON — only until the first successful load
  // ═══════════════════════════════════════════════════════════
  if (!pageReady && isInitialLoading) {
    return (
      <div style={styles.container} className="dashboard-page">
        <style>{HIDE_SCROLLBARS_CSS}</style>
        <SkeletonStyles theme={dark ? "dark" : "light"} />
        <LoadingPill
          text={isRTL ? "جارٍ تحميل بيانات لوحة التحكم..." : "Loading dashboard information..."}
          theme={dark ? "dark" : "light"}
          isRTL={isRTL}
        />

        {/* Welcome header skeleton — welcomeCard carries a large negative
            marginTop meant to tuck it under the real topbar; neutralize it
            here so it doesn't slide up over the LoadingPill above it. */}
        <div style={{ ...styles.welcomeCard, marginTop: 0 }}>
          <div style={styles.contentSection}>
            <SkeletonBox width="240px" height="30px" style={{ marginBottom: "12px" }} />
            <SkeletonBox width="300px" height="18px" />
          </div>
        </div>

        {/* Stats grid skeleton */}
        <div style={styles.statsGrid}>
          {[1, 2, 3, 4].map((i) => (
            <SkeletonStatCard key={i} cardStyle={styles.statCard} />
          ))}
        </div>

        {/* Employee distribution skeleton */}
        <div style={{ marginTop: "24px", marginBottom: "24px" }}>
          <SkeletonBox width="100%" height="280px" style={{ borderRadius: "16px" }} />
        </div>

        {/* Activity feed skeleton */}
        <div style={{ marginBottom: "24px" }}>
          <SkeletonBox width="100%" height="320px" style={{ borderRadius: "16px" }} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ ...styles.container, paddingTop: 24 }}>
        <LoadErrorState error={error} onRetry={onRetry} />
      </div>
    );
  }

  return (
    <div style={styles.container} ref={containerRef} className="dashboard-page">
      {/* marginTop: -50 (shared welcomeCard style) tucks the card under the
          topbar and clips its top edge on this page — neutralize it here. */}
      <WelcomeCard
        styles={{
          ...styles,
          // The shared welcomeCard style is a plain block, which drops the
          // role badge onto its own line under the title. Lay the card out as
          // one row — title at the start, badge at the end, both centered.
          welcomeCard: {
            ...styles.welcomeCard,
            marginTop: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "12px 24px",
          },
          welcomeTitle: { ...styles.welcomeTitle, margin: 0 },
        }}
        text={text}
        language={language}
        userName={displayNameShort}
        title={displayTitle}
        subtitle={language === "ar" ? " " : " "}
      >
        {roleBadge && (
          <div
            title={roleBadge.label}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 14px",
              borderRadius: "9999px",
              background: roleBadge.tint,
              border: `1px solid ${roleBadge.ring}`,
              color: roleBadge.color,
              whiteSpace: "nowrap",
              maxWidth: "100%",
            }}
          >
            <roleBadge.Icon size={16} style={{ flexShrink: 0 }} />
            <span style={{ fontSize: "13px", fontWeight: 700, lineHeight: 1.2 }}>
              {roleBadge.label}
            </span>

          </div>
        )}
      </WelcomeCard>

      <StatsGrid styles={styles} stats={stats} screenSize={screenSize} layout="inline" />

      {/* Attendance Distribution + Employee Distribution */}
      {((showAttendance && attendanceData.length > 0) ||
        (showDepartments && donutData1.length > 0)) && (
        <div
          style={{
            marginTop: "24px",
            marginBottom: "24px",
            display: "grid",
            gridTemplateColumns:
              screenSize.isMobile ||
              !(showAttendance && attendanceData.length > 0) ||
              !(showDepartments && donutData1.length > 0)
                ? "1fr"
                : "2fr 3fr",
            gap: "20px",
            // Stretch both cards to the same height so the row reads as one
            // aligned band instead of a short card next to a tall one.
            alignItems: "stretch",
          }}
        >
          {showAttendance && attendanceData.length > 0 && (
            <AttendanceDistributionChart
              attendanceData={attendanceData}
              previousMonthData={previousMonthData}
              theme={theme}
              language={language}
            />
          )}

          {showDepartments && donutData1.length > 0 && (
            <div
              style={{
                background: dark ? "rgba(58, 58, 58, 0.02)" : "rgba(255,255,255,0.9)",
                borderRadius: "16px",
                border: dark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.06)",
                boxShadow: dark
                  ? "0 8px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.06)"
                  : "0 8px 32px rgba(0,0,0,0.08)",
                padding: screenSize?.isMobile ? "16px" : "24px",
                WebkitBackdropFilter: dark ? "blur(20px) saturate(160%)" : "blur(8px)",
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", borderBottom: dark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)", paddingBottom: "16px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
                  <icons.PieChart size={screenSize?.isMobile ? 20 : 24} color={dark ? "rgba(255,255,255,0.7)" : "#555"} />
                  <h3 style={{
                    margin: 0, fontSize: screenSize?.isMobile ? "16px" : "20px", fontWeight: 700,
                    color: dark ? "#fff" : "#111",
                  }}>
                    {language === "ar" ? "توزيع الموظفين" : "Employee Distribution"}
                  </h3>
                </div>

                {/* Departments badge — mirrors the Total badge on the Attendance card */}
                <div
                  style={{
                    padding: "6px 14px",
                    borderRadius: "20px",
                    background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
                    border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  <span style={{ fontSize: "12px", fontWeight: 600, color: dark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)" }}>
                    {language === "ar" ? "الأقسام: " : "Departments: "}
                  </span>
                  <span style={{ fontSize: "14px", fontWeight: 700, color: dark ? "#f1f5f9" : "#1e293b" }}>
                    {sorted.filter((d) => Number(d.count) > 0 && !/^unassigned$/i.test(String(d.name || "").trim())).length}
                  </span>
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "stretch", paddingTop: "8px", minWidth: 0 }}>
                <EmployeeDistributionChart
                  data={sorted}
                  theme={theme}
                  language={language}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Hiring trend + recent activities (visibility-scoped). When Recent
          Activity has nothing to show, Employees by company fills that
          column instead so the row isn't left half-empty; in that case the
          standalone full-width block below is skipped. */}
      {(() => {
        const hiringVisible = showHiring && scopedStats?.hiringTrend?.length > 0;
        const activitiesVisible =
          showActivities && Array.isArray(effRecentActivities) && effRecentActivities.length > 0;
        const companiesVisible = showCompanies && (scopedStats?.companies?.length || 0) > 1;
        const companiesInActivityRow = companiesVisible && !activitiesVisible;

        return (
          <>
            {(hiringVisible || activitiesVisible || companiesInActivityRow) && (
              <div
                style={{
                  marginBottom: "24px",
                  display: "grid",
                  gridTemplateColumns:
                    screenSize.isMobile || !hiringVisible || !(activitiesVisible || companiesInActivityRow)
                      ? "1fr"
                      : "3fr 2fr",
                  gap: "20px",
                  // Stretch so the shorter Hiring Trend card matches Recent
                  // Activity's height instead of shrinking to fit its own content.
                  alignItems: "stretch",
                }}
              >
                {hiringVisible && (
                  <div style={{ ...chartCard, minWidth: 0, padding: screenSize?.isMobile ? "16px" : "20px" }}>
                    <h3 style={sectionTitle}>
                      {language === "ar" ? "التعيينات الجديدة (آخر ٦ أشهر)" : "New hires (last 6 months)"}
                    </h3>
                    <HiringTrendChart data={scopedStats.hiringTrend} theme={theme} language={language} />
                  </div>
                )}

                {activitiesVisible && (
                  <div style={{ minWidth: 0 }}>
                    <ActivityFeed
                      styles={styles}
                      text={text}
                      language={language}
                      recentActivities={effRecentActivities}
                      screenSize={screenSize}
                    />
                  </div>
                )}

                {companiesInActivityRow && (
                  <div style={{ ...chartCard, minWidth: 0, padding: screenSize?.isMobile ? "16px" : "18px 20px" }}>
                    <h3 style={{ ...sectionTitle, fontSize: "14px", marginBottom: "10px" }}>
                      {language === "ar" ? "توزيع الشركات" : "Employees by company"}
                    </h3>
                    <CompaniesSplitBar data={scopedStats.companies} theme={theme} language={language} />
                  </div>
                )}
              </div>
            )}

            {companiesVisible && !companiesInActivityRow && (
              <div
                style={{
                  ...chartCard,
                  height: "auto",
                  minWidth: 0,
                  marginBottom: "24px",
                  padding: screenSize?.isMobile ? "16px" : "18px 20px",
                }}
              >
                <h3 style={{ ...sectionTitle, fontSize: "14px", marginBottom: "10px" }}>
                  {language === "ar" ? "توزيع الشركات" : "Employees by company"}
                </h3>
                <CompaniesSplitBar data={scopedStats.companies} theme={theme} language={language} />
              </div>
            )}
          </>
        );
      })()}


      <style>
        {`
          ${HIDE_SCROLLBARS_CSS}
          * {
            -webkit-tap-highlight-color: transparent;
          }

          @media (max-width: 768px) {
            * {
              text-rendering: optimizeSpeed;
            }
          }

          @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
            * {
              text-rendering: optimizeLegibility;
            }
          }

          @media (prefers-reduced-motion: reduce) {
            * {
              animation-duration: 0.01ms !important;
              animation-iteration-count: 1 !important;
              transition-duration: 0.01ms !important;
            }
          }

          *:focus-visible {
            outline: 2px solid ${theme === "dark" ? "#3b82f6" : "#2563eb"};
            outline-offset: 2px;
            border-radius: 4px;
          }
        `}
      </style>
    </div>
  );
};

// "Try again" remounts the dashboard content, so every one of its loaders
// runs again from scratch (they load on mount).
const Dashboard = (props) => {
  const [attempt, setAttempt] = useState(0);
  return <DashboardContent key={attempt} {...props} onRetry={() => setAttempt((n) => n + 1)} />;
};

export default Dashboard;
